// Promises API from the main 'fs' specifier — a `* as fs from 'fs/promises'`
// namespace import is mangled to `undefined` in the bundled server dist (only
// the main 'fs'/'path'/'crypto' builtins are externalized), which would break
// every fs call here at runtime. See AssetStore.ts for the same fix.
import { promises as fs } from 'fs';
import * as fssync from 'fs';
import * as PATH from 'path';
import type { AssetSource, AssetEntry, LoadedAsset } from '@gotti/core';
import { directoryTree, NodeType, type FileSystemTree } from '../AssetReader.js';

export interface WritableAssetSource extends AssetSource {
  write(path: string, bytes: Uint8Array): Promise<void>;
  delete(path: string): Promise<void>;
}

const MIME: Record<string, string> = { '.json': 'application/json' };
function mimeFromExt(p: string): string {
  return MIME[PATH.extname(p).toLowerCase()] ?? 'application/octet-stream';
}

export class FsAssetSource implements WritableAssetSource {
  constructor(private readonly root: string) {}

  private abs(rel: string): string {
    const rootResolved = PATH.resolve(this.root);
    const abs = PATH.resolve(rootResolved, rel);
    if (abs !== rootResolved && !abs.startsWith(rootResolved + PATH.sep)) {
      throw new Error(`FsAssetSource: path "${rel}" escapes the assets root.`);
    }
    return abs;
  }

  async load(rel: string): Promise<LoadedAsset> {
    const buf = await fs.readFile(this.abs(rel));
    return { path: rel, mimeType: mimeFromExt(rel), bytes: new Uint8Array(buf) };
  }

  async write(rel: string, bytes: Uint8Array): Promise<void> {
    const abs = this.abs(rel);
    await fs.mkdir(PATH.dirname(abs), { recursive: true });
    await fs.writeFile(abs, bytes);
  }

  async delete(rel: string): Promise<void> {
    try {
      await fs.unlink(this.abs(rel));
    } catch (e: any) {
      // Already-gone is fine (idempotent delete); anything else is a real
      // failure (permissions, I/O error, etc.) and must propagate so callers
      // (e.g. ServerAssetManager.remove) don't report success on a failure.
      if (e?.code !== 'ENOENT') throw e;
    }
  }

  manifest(): AssetEntry[] {
    if (!fssync.existsSync(this.root)) return [];
    const tree = directoryTree(this.root, { attributes: ['type'] });
    const out: AssetEntry[] = [];
    const walk = (node: FileSystemTree) => {
      if (!node) return;
      if (node.type === NodeType.FILE) {
        const rel = PATH.relative(this.root, node.path);
        out.push({ path: rel, mimeType: mimeFromExt(rel), size: node.size ?? 0, hash: '' });
      }
      node.children?.forEach(walk);
    };
    walk(tree);
    return out;
  }
}
